/**
 * The day, hour by hour.
 *
 * Arrivals rise above the line, departures hang below it, on one scale. Each
 * column is solid for what has happened and hatched for what is still to
 * come, and a dashed outline shows what the schedule put in that hour — so
 * an hour carrying more than it was due shows the delays pushed into it.
 * The line marked "now" is the clock.
 *
 * Point at an hour for its numbers; click it (or Enter on the keyboard) for
 * the movements in it. Arrow keys walk the hours. Every number is also in the
 * table view, so nothing is reached by hovering alone.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { bestKnown, flightName, type Flight } from "../../domain/flights";
import { arrivals, dayShape, departures, hourNow, hourSlots, lateBy, total, type HourBasis, type HourSlot } from "../../domain/hourly";
import { opsState } from "../../domain/flightDelays";
import { placeFor } from "../../domain/fidsPublic";
import { Segmented } from "../../design/components";
import { t, tn } from "../../i18n";
import { hhmm } from "../../lib/format";
import { OpsPill } from "./FlightTheme";
import c from "./HourlyChart.module.css";

const H = 300;
const PAD = { top: 26, bottom: 40, left: 40, right: 12 };
const KIND: Record<string, string> = { actual: "actual", estimated: "estimated", scheduled: "scheduled", none: "" };

const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;

/** A whole-number scale with three or four steps either side of the line. */
function niceMax(n: number): { max: number; step: number } {
  const m = Math.max(2, n);
  const step = m <= 4 ? 1 : m <= 8 ? 2 : m <= 20 ? 5 : 10;
  return { max: Math.ceil(m / step) * step, step };
}

/** A bar anchored at the baseline, rounded only at its far end. */
function bar(x: number, w: number, y0: number, len: number, up: boolean): string {
  if (len <= 0) return "";
  const r = Math.min(4, w / 2, len);
  const y1 = up ? y0 - len : y0 + len;
  return up
    ? `M${x},${y0} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + w - r} Q${x + w},${y1} ${x + w},${y1 + r} V${y0} Z`
    : `M${x},${y0} V${y1 - r} Q${x},${y1} ${x + r},${y1} H${x + w - r} Q${x + w},${y1} ${x + w},${y1 - r} V${y0} Z`;
}

export function HourlyChart({ flights, offsetHours }: { flights: readonly Flight[]; offsetHours: number }) {
  const [basis, setBasis] = useState<HourBasis>("best");
  const [view, setView] = useState<"chart" | "table">("chart");
  const [hover, setHover] = useState<number | null>(null);
  const [picked, setPicked] = useState<number | null>(null);
  const [width, setWidth] = useState(900);
  const box = useRef<HTMLDivElement>(null);
  const now = new Date();

  /* the chart is drawn at the width it has, so text stays the size it is meant to be */
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(320, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [view]);

  const slots = useMemo(() => hourSlots(flights, offsetHours, basis), [flights, offsetHours, basis]);
  const shape = useMemo(() => dayShape(slots), [slots]);
  const top = Math.max(...slots.map((s) => Math.max(arrivals(s), departures(s), s.arrScheduled, s.depScheduled)));
  const { max, step } = niceMax(top);

  const plotW = width - PAD.left - PAD.right;
  const band = plotW / 24;
  const bw = Math.max(6, band * 0.62);
  const y0 = PAD.top + (H - PAD.top - PAD.bottom) / 2;
  const half = (H - PAD.top - PAD.bottom) / 2 - 4;
  const len = (n: number) => (n / max) * half;
  const xOf = (h: number) => PAD.left + h * band;
  const nowX = PAD.left + hourNow(now, offsetHours) * band;
  const ticks: number[] = [];
  for (let v = step; v <= max; v += step) ticks.push(v);

  const shown = hover ?? picked;
  const tip = shown !== null ? slots[shown] : null;

  function onKey(e: KeyboardEvent, h: number) {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = (h + (e.key === "ArrowRight" ? 1 : 23)) % 24;
      (box.current?.querySelector(`[data-hour="${next}"]`) as SVGElement | null)?.focus();
      if (picked !== null) setPicked(next);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setPicked((p) => (p === h ? null : h));
    } else if (e.key === "Escape") setPicked(null);
  }

  return (
    <div className={c.root} data-testid="hourly">
      <div className={c.head}>
        <p className={c.headline} data-testid="hourly-headline">
          {shape.peak ? (
            <>
              {t("Busiest at {h}", { h: hh(shape.peak.hour) })}: <b>{total(shape.peak)}</b>{" "}
              {t("movements ({a} in, {d} out)", { a: arrivals(shape.peak), d: departures(shape.peak) })}
              <span className={c.sep}>·</span>
              {tn(shape.toCome, "{n} still to come today", "{n} still to come today")}
              {basis === "best" && shape.pushedInto.length > 0 && (
                <>
                  <span className={c.sep}>·</span>
                  {tn(
                    shape.pushedInto.length,
                    "{n} hour carrying more than the schedule put in it",
                    "{n} hours carrying more than the schedule put in them",
                  )}
                </>
              )}
            </>
          ) : (
            t("Nothing is scheduled today.")
          )}
        </p>
        <div className={c.controls}>
          <Segmented
            ariaLabel="Count each movement in"
            value={basis}
            onChange={setBasis}
            options={[
              { value: "best", label: "the hour it happens" },
              { value: "scheduled", label: "the hour it was due" },
            ]}
          />
          <Segmented
            ariaLabel="Chart or table"
            value={view}
            onChange={setView}
            options={[
              { value: "chart", label: "Chart" },
              { value: "table", label: "Table" },
            ]}
          />
        </div>
      </div>

      <div className={c.legend} aria-hidden>
        <span data-k="arr">{t("Arrivals")}</span>
        <span data-k="dep">{t("Departures")}</span>
        <span data-k="done">{t("happened")}</span>
        <span data-k="tocome">{t("still to come")}</span>
        {basis === "best" && <span data-k="sched">{t("as scheduled")}</span>}
        <span data-k="now">{t("now")}</span>
      </div>

      {view === "chart" ? (
        <div ref={box} className={c.plot} onPointerLeave={() => setHover(null)}>
          <svg width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img" aria-label={t("Movements by hour of the day")} data-testid="hourly-chart">
            <defs>
              <pattern id="hatch-arr" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="6" height="6" className={c.arrSoft} />
                <line x1="0" y1="0" x2="0" y2="6" className={c.arrHatch} />
              </pattern>
              <pattern id="hatch-dep" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(135)">
                <rect width="6" height="6" className={c.depSoft} />
                <line x1="0" y1="0" x2="0" y2="6" className={c.depHatch} />
              </pattern>
            </defs>

            {/* recessive grid, one scale both ways */}
            {ticks.map((v) => (
              <g key={v} className={c.grid}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y0 - len(v)} y2={y0 - len(v)} />
                <line x1={PAD.left} x2={width - PAD.right} y1={y0 + len(v)} y2={y0 + len(v)} />
                <text x={PAD.left - 8} y={y0 - len(v)} dy="0.35em">
                  {v}
                </text>
                <text x={PAD.left - 8} y={y0 + len(v)} dy="0.35em">
                  {v}
                </text>
              </g>
            ))}
            <text x={PAD.left + 4} y={PAD.top - 10} className={c.sideLabel}>
              ▲ {t("arrivals")}
            </text>
            <text x={PAD.left + 4} y={H - PAD.bottom + 2} className={c.sideLabel}>
              ▼ {t("departures")}
            </text>

            {slots.map((s) => {
              const x = xOf(s.hour) + (band - bw) / 2;
              const on = shown === s.hour;
              const dim = shown !== null && !on;
              const aDone = len(s.arrDone);
              const aAll = len(arrivals(s));
              const dDone = len(s.depDone);
              const dAll = len(departures(s));
              return (
                <g key={s.hour} className={c.col} data-on={on} data-dim={dim} data-peak={shape.peak?.hour === s.hour}>
                  {/* the hour's selected wash */}
                  {picked === s.hour && <rect x={xOf(s.hour)} y={PAD.top - 4} width={band} height={H - PAD.top - PAD.bottom + 8} className={c.pickWash} />}
                  {/* done near the line, still to come beyond it, a 2px gap between */}
                  <path d={bar(x, bw, y0 - 1, aDone, true)} className={c.arr} />
                  {aAll > aDone && <path d={bar(x, bw, y0 - 1 - aDone - (aDone ? 2 : 0), aAll - aDone - (aDone ? 2 : 0), true)} fill="url(#hatch-arr)" className={c.arrEdge} />}
                  <path d={bar(x, bw, y0 + 1, dDone, false)} className={c.dep} />
                  {dAll > dDone && <path d={bar(x, bw, y0 + 1 + dDone + (dDone ? 2 : 0), dAll - dDone - (dDone ? 2 : 0), false)} fill="url(#hatch-dep)" className={c.depEdge} />}
                  {/* what the schedule put here */}
                  {basis === "best" && s.arrScheduled > 0 && (
                    <rect x={x - 2} y={y0 - 1 - len(s.arrScheduled)} width={bw + 4} height={len(s.arrScheduled)} className={c.sched} />
                  )}
                  {basis === "best" && s.depScheduled > 0 && (
                    <rect x={x - 2} y={y0 + 1} width={bw + 4} height={len(s.depScheduled)} className={c.sched} />
                  )}
                  {/* selective labels: the peak and the hour in hand */}
                  {(on || shape.peak?.hour === s.hour) && arrivals(s) > 0 && (
                    <text x={x + bw / 2} y={y0 - aAll - 6} className={c.value}>
                      {arrivals(s)}
                    </text>
                  )}
                  {(on || shape.peak?.hour === s.hour) && departures(s) > 0 && (
                    <text x={x + bw / 2} y={y0 + dAll + 14} className={c.value}>
                      {departures(s)}
                    </text>
                  )}
                  {s.cancelled > 0 && (
                    <text x={x + bw / 2} y={y0 + 4} className={c.cancel} aria-hidden>
                      ✕
                    </text>
                  )}
                </g>
              );
            })}

            {/* the line and the hours along it */}
            <line x1={PAD.left} x2={width - PAD.right} y1={y0} y2={y0} className={c.baseline} />
            {slots.map((s) =>
              s.hour % (width < 640 ? 3 : 2) === 0 ? (
                <text key={s.hour} x={xOf(s.hour) + band / 2} y={H - 10} className={c.hourLabel}>
                  {String(s.hour).padStart(2, "0")}
                </text>
              ) : null,
            )}

            {/* now */}
            <line x1={nowX} x2={nowX} y1={PAD.top - 8} y2={H - PAD.bottom + 4} className={c.now} />
            <text x={nowX} y={PAD.top - 12} className={c.nowLabel}>
              {t("now")} {hhmm(now.toISOString())}
            </text>

            {/* the hit targets: the whole column, larger than the mark */}
            {slots.map((s) => (
              <rect
                key={`hit${s.hour}`}
                x={xOf(s.hour)}
                y={PAD.top - 6}
                width={band}
                height={H - PAD.top - PAD.bottom + 12}
                className={c.hit}
                tabIndex={0}
                role="button"
                aria-pressed={picked === s.hour}
                aria-label={t("{h}: {a} arrivals, {d} departures", { h: hh(s.hour), a: arrivals(s), d: departures(s) })}
                data-hour={s.hour}
                data-testid={`hour-${s.hour}`}
                onPointerEnter={() => setHover(s.hour)}
                onFocus={() => setHover(s.hour)}
                onBlur={() => setHover(null)}
                onClick={() => setPicked((p) => (p === s.hour ? null : s.hour))}
                onKeyDown={(e) => onKey(e, s.hour)}
              />
            ))}
          </svg>

          {tip && hover !== null && (
            <div
              className={c.tip}
              role="status"
              style={{
                left: Math.min(Math.max(xOf(tip.hour) + band / 2, 110), width - 110),
                top: tip.arrDone + tip.arrToCome > 0 ? 6 : y0 - 40,
              }}
            >
              <div className={c.tipHead}>
                {hh(tip.hour)}–{hh((tip.hour + 1) % 24)}
              </div>
              <div className={c.tipRow} data-k="arr">
                <b>{arrivals(tip)}</b> {t("arrivals")}
                {tip.arrToCome > 0 && <small>{t("{n} to come", { n: tip.arrToCome })}</small>}
              </div>
              <div className={c.tipRow} data-k="dep">
                <b>{departures(tip)}</b> {t("departures")}
                {tip.depToCome > 0 && <small>{t("{n} to come", { n: tip.depToCome })}</small>}
              </div>
              {basis === "best" && (
                <div className={c.tipMuted}>
                  {t("scheduled here: {a} in, {d} out", { a: tip.arrScheduled, d: tip.depScheduled })}
                </div>
              )}
              {(tip.late > 0 || tip.cancelled > 0) && (
                <div className={c.tipWarn}>
                  {[tip.late ? tn(tip.late, "{n} late", "{n} late") : null, tip.cancelled ? tn(tip.cancelled, "{n} cancelled", "{n} canceled") : null]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
              <div className={c.tipHint}>{picked === tip.hour ? t("click again to close") : t("click to see the flights")}</div>
            </div>
          )}
        </div>
      ) : (
        <div className={c.tableWrap}>
          <table className={c.table} data-testid="hourly-table">
            <thead>
              <tr>
                <th>{t("Hour")}</th>
                <th className={c.num}>{t("Arrivals")}</th>
                <th className={c.num}>{t("Departures")}</th>
                <th className={c.num}>{t("Still to come")}</th>
                <th className={c.num}>{t("Scheduled")}</th>
                <th className={c.num}>{t("Late")}</th>
                <th className={c.num}>{t("Canceled")}</th>
              </tr>
            </thead>
            <tbody>
              {slots
                .filter((s) => total(s) || s.cancelled || s.arrScheduled || s.depScheduled)
                .map((s) => (
                  <tr key={s.hour} onClick={() => setPicked(s.hour)} data-on={picked === s.hour}>
                    <td className={c.mono}>{hh(s.hour)}</td>
                    <td className={c.num}>{arrivals(s)}</td>
                    <td className={c.num}>{departures(s)}</td>
                    <td className={c.num}>{s.arrToCome + s.depToCome}</td>
                    <td className={c.num}>{s.arrScheduled + s.depScheduled}</td>
                    <td className={c.num}>{s.late || ""}</td>
                    <td className={c.num}>{s.cancelled || ""}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {picked !== null && <HourDetail slot={slots[picked]} basis={basis} onClose={() => setPicked(null)} now={now} />}
    </div>
  );
}

/* ── the hour opened ──────────────────────────────────────────────────── */

function HourDetail({ slot, basis, onClose, now }: { slot: HourSlot; basis: HourBasis; onClose: () => void; now: Date }) {
  const pax = slot.passengers;
  return (
    <section className={c.detail} data-testid="hourly-detail" aria-label={t("Movements at {h}", { h: hh(slot.hour) })}>
      <div className={c.detailHead}>
        <div>
          <h4>
            {hh(slot.hour)}–{hh((slot.hour + 1) % 24)}
          </h4>
          <p>
            {tn(arrivals(slot), "{n} arrival", "{n} arrivals")} · {tn(departures(slot), "{n} departure", "{n} departures")}
            {slot.late > 0 && ` · ${tn(slot.late, "{n} late", "{n} late")}`}
            {pax > 0 && ` · ${t("{n} passengers filed", { n: pax.toLocaleString() })}`}
            {basis === "best" && ` · ${t("schedule had {n} here", { n: slot.arrScheduled + slot.depScheduled })}`}
          </p>
        </div>
        <button type="button" className={c.close} onClick={onClose} aria-label={t("Close")} data-testid="hourly-close">
          ✕
        </button>
      </div>
      {slot.flights.length === 0 && slot.cancelledFlights.length === 0 ? (
        <p className={c.empty}>{t("No movements in this hour.")}</p>
      ) : (
        <div className={c.tableWrap}>
          <table className={c.table}>
            <thead>
              <tr>
                <th>{t("Time")}</th>
                <th>{t("Flight")}</th>
                <th>{t("From / to")}</th>
                <th>{t("Aircraft")}</th>
                <th className={c.num}>{t("Late by")}</th>
                <th>{t("State")}</th>
              </tr>
            </thead>
            <tbody>
              {slot.flights.map((f) => {
                const b = bestKnown(f);
                const late = lateBy(f);
                const place = placeFor(f.otherAirport);
                return (
                  <tr key={f.id} data-testid={`hourly-row-${flightName(f)}`}>
                    <td className={c.mono}>
                      {hhmm(b.at)} <small className={c.kind}>{t(KIND[b.kind] ?? "")}</small>
                      {b.kind !== "scheduled" && f.scheduledAt && b.at !== f.scheduledAt && (
                        <small className={c.was}>
                          {t("due")} {hhmm(f.scheduledAt)}
                        </small>
                      )}
                    </td>
                    <td className={c.mono}>
                      <b>{flightName(f)}</b> <small className={c.dir}>{f.direction === "arrival" ? "▲" : "▼"}</small>
                    </td>
                    <td>{place ? `${place.en} (${f.otherAirport})` : (f.otherAirport ?? "")}</td>
                    <td className={c.mono}>{f.aircraftIcao ?? ""}</td>
                    <td className={c.num}>{late !== null ? t("{n} min", { n: late }) : ""}</td>
                    <td>
                      <OpsPill state={opsState(f, now)} />
                    </td>
                  </tr>
                );
              })}
              {slot.cancelledFlights.map((f) => (
                <tr key={f.id} className={c.cancelledRow}>
                  <td className={c.mono}>{hhmm(f.scheduledAt)}</td>
                  <td className={c.mono}>
                    <b>{flightName(f)}</b> <small className={c.dir}>{f.direction === "arrival" ? "▲" : "▼"}</small>
                  </td>
                  <td>{placeFor(f.otherAirport)?.en ?? f.otherAirport ?? ""}</td>
                  <td className={c.mono}>{f.aircraftIcao ?? ""}</td>
                  <td />
                  <td>
                    <OpsPill state="cancelled" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
